import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { shopApi, productLabel } from "@/lib/shopApi";
import { money } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { ProductTile } from "@/components/store/Brand";
import { StockPill } from "@/pages/store/Shop";

export function QtyStepper({ value, onChange, testid }) {
  return (
    <div className="inline-flex h-11 items-center rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      <button onClick={() => onChange(value - 1)} className="flex h-full w-10 items-center justify-center text-slate-600 hover:text-slate-900" data-testid={`${testid}-minus`} aria-label="Decrease"><Minus className="h-4 w-4" /></button>
      <span className="w-8 text-center font-semibold" data-testid={`${testid}-value`}>{value}</span>
      <button onClick={() => onChange(value + 1)} className="flex h-full w-10 items-center justify-center text-slate-600 hover:text-slate-900" data-testid={`${testid}-plus`} aria-label="Increase"><Plus className="h-4 w-4" /></button>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useMember();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(null);

  useEffect(() => { shopApi.get(`/shop/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="py-20 text-center text-slate-500" data-testid="product-not-found">This product is not available. <Link to="/shop" className="font-semibold text-sky-700">Back to shop</Link></div>;
  if (!p) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
      <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:gap-14">
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-white"><ProductTile product={p} imageId={active} /></div>
          {p.image_ids?.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto" data-testid="product-gallery">
              {p.image_ids.map((iid) => (
                <button key={iid} onClick={() => setActive(iid)} data-testid={`product-thumb-${iid}`}
                  className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${(active || p.image_ids[0]) === iid ? "border-sky-400" : "border-slate-200 hover:border-slate-400"}`}>
                  <ProductTile product={p} imageId={iid} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="animate-[fadeUp_.4s_ease-out]">
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="product-detail-name">{p.name}</h1>
          {p.size && <span className="mt-3 inline-block rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 font-mono text-sm font-semibold text-sky-800" data-testid="product-detail-size">{p.size}</span>}
          <div className="mt-5 flex items-center gap-4">
            <span className="font-head text-3xl font-extrabold text-slate-900" data-testid="product-detail-price">{money(p.price)} <span className="text-base font-semibold text-slate-500">AUD</span></span>
            <StockPill inStock={p.in_stock} />
          </div>
          <p className="mt-6 whitespace-pre-line leading-relaxed text-slate-600" data-testid="product-detail-description">{p.description}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <QtyStepper value={qty} onChange={(v) => setQty(Math.max(1, Math.min(99, v)))} testid="product-qty" />
            <button disabled={!p.in_stock} onClick={() => { addToCart(p, qty); toast.success(`${productLabel(p)} × ${qty} added to cart`); }} data-testid="product-add-to-cart"
              className="h-11 flex-1 rounded-lg bg-[#89CFF0] px-6 font-semibold text-slate-900 transition-colors hover:bg-[#74C2EC] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 sm:flex-none">
              {p.in_stock ? "Add to cart" : "Out of stock"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
